// LocalStorage-backed history of mood cards created in this browser.
// Anonymous — no server-side user account required.

const KEY = "moodcard.history.v1";
const LIMIT = 50;

const safeRead = () => {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const safeWrite = (list) => {
    try {
        localStorage.setItem(KEY, JSON.stringify(list.slice(0, LIMIT)));
    } catch {
        /* quota / private mode — silently ignore */
    }
};

export const mergeServerHistory = (serverItems) => {
    if (!Array.isArray(serverItems) || serverItems.length === 0) return safeRead();
    const local = safeRead();
    const localById = new Map(local.map((m) => [m.id, m]));
    for (const s of serverItems) {
        if (!s?.id) continue;
        // Server wins on metadata but preserve local position-by-recency by created_at
        localById.set(s.id, {
            id: s.id,
            feeling: s.feeling,
            selected_style: s.selected_style || localById.get(s.id)?.selected_style || null,
            created_at: s.created_at || localById.get(s.id)?.created_at,
            preview_style: s.preview_style || null,
            preview_bg: s.preview_bg || null,
        });
    }
    const merged = Array.from(localById.values()).sort((a, b) => {
        const da = new Date(a.created_at || 0).getTime();
        const db = new Date(b.created_at || 0).getTime();
        return db - da;
    });
    safeWrite(merged);
    return merged;
};

export const getHistory = () => safeRead();

export const addToHistory = (mood) => {
    if (!mood?.id) return;
    const list = safeRead();
    const filtered = list.filter((m) => m.id !== mood.id);
    filtered.unshift({
        id: mood.id,
        slug: mood.slug || null,
        feeling: mood.feeling,
        selected_style: mood.selected_style || null,
        created_at: mood.created_at || new Date().toISOString(),
        preview_style: mood.cards?.[0]?.style || null,
        preview_bg: mood.cards?.[0]?.background_url || null,
        preview_title: mood.cards?.[0]?.title || null,
        preview_body: mood.cards?.[0]?.body || null,
    });
    safeWrite(filtered);
};

export const updateHistoryStyle = (id, style) => {
    const list = safeRead();
    const idx = list.findIndex((m) => m.id === id);
    if (idx === -1) return;
    list[idx] = { ...list[idx], selected_style: style };
    safeWrite(list);
};

export const removeFromHistory = (id) => {
    safeWrite(safeRead().filter((m) => m.id !== id));
};

export const clearHistory = () => {
    try {
        localStorage.removeItem(KEY);
    } catch {
        /* ignore */
    }
};
